/**
 * Queue Health tile threshold logic.
 *
 * US-24-GR-39: extracted out of `QueueHealthPage.tsx` so tests can import the REAL
 * implementation. It previously lived inside the page as module-private functions, which left
 * `src/test/queueHealth.test.ts` re-declaring its own copies — and a test that owns a copy of the
 * logic is tautological: it passes whether the page wires the tiles up correctly or not, which is
 * exactly why it stayed green for the whole life of the bug this story fixes.
 *
 * These deliberately do NOT live in the page file. Exporting non-component values from a `.tsx`
 * page trips `react-refresh/only-export-components` and degrades fast refresh for everyone editing
 * that page.
 *
 * ⚠️ Importing these in a test is necessary but NOT sufficient. The defect was never in these
 * functions — each was correct in isolation — it was in *which one the page called* for the events
 * tiles. Only a render-level test can observe that; see `QueueHealthPage.thresholds.test.tsx`.
 */

import type { AlertThresholdConfig } from '../api/orchestratorClient';

export type Threshold = 'healthy' | 'warn' | 'alert';

export function thresholdFor(depth: number, warn: number, alert: number): Threshold {
  if (depth > alert) return 'alert';
  if (depth > warn) return 'warn';
  return 'healthy';
}

export function queueThreshold(depth: number, config: AlertThresholdConfig): Threshold {
  return thresholdFor(depth, config.queueDepthWarn, config.queueDepthAlert);
}

export function dlqThreshold(depth: number, config: AlertThresholdConfig): Threshold {
  return thresholdFor(depth, config.dlqDepthWarn, config.dlqDepthAlert);
}

/** The agent-events queue tile — colours off `eventsQueueDepth*`, never the agent-tasks fields. */
export function computeEventsQueueThreshold(
  depth: number,
  config: AlertThresholdConfig,
): Threshold {
  return thresholdFor(depth, config.eventsQueueDepthWarn, config.eventsQueueDepthAlert);
}

/** The agent-events DLQ tile — colours off `eventsDlqDepth*`, never the agent-tasks fields. */
export function computeEventsDlqThreshold(
  depth: number,
  config: AlertThresholdConfig,
): Threshold {
  return thresholdFor(depth, config.eventsDlqDepthWarn, config.eventsDlqDepthAlert);
}
